import {Injectable}                  from '@angular/core';
import {LicenseStore}                from '../../../stores/farm/license.store';
import {CampaignYearService}         from '../campaign-year.service';
import {CampaignYearStore}           from '../../../stores/login/campaignyear.store';
import {ApFeatureModulKey}           from '../../../ap-interface/enums/ap-feature-modul-key.enum';
import {LoginStore}                  from '../../../stores/login/login.store';
import {ApDateService, DateDiffType} from '../../../ap-core/services/ap-date-service';
import ILicense = Data.Licensing.ILicense;

@Injectable({providedIn: 'root'})
export class LicenseService {
  constructor(private loginStore: LoginStore,
              private licenseStore: LicenseStore,
              private campaignYearStore: CampaignYearStore,
              private dateService: ApDateService,
              private campaignYearService: CampaignYearService) {
  }

  /**
   * Retrieves a valid satellite license
   * @returns Valid satellite license or undefined
   * @public
   */
  public getValidSatelliteLicense(): ILicense | undefined {
    return this._getLicense(ApFeatureModulKey.Satellite);
  }

  /**
   * Retrieves a valid base fertilization license
   * @returns Valid base fertilization license or undefined
   * @public
   */
  public getValidBaseFertilizationLicense(): ILicense | undefined {
    return this._getLicense(ApFeatureModulKey.BaseFertilization);
  }

  /**
   * Retrieves a valid nitrogen fertilization license
   * @returns Valid nitrogen fertilization license or undefined
   * @public
   */
  public getValidNFertilizationLicense(): ILicense | undefined {
    return this._getLicense(ApFeatureModulKey.NFertilization);
  }

  /**
   * Retrieves a valid plant protection license
   * @returns Valid plant protection license or undefined
   * @public
   */
  public getValidPlantProtectionLicense(): ILicense | undefined {
    return this._getLicense(ApFeatureModulKey.PlantProtection);
  }

  /**
   * Checks if the selected campaign year is the latest licensed year
   * Validates campaign year based on:
   * - Current farm date
   * - Campaign year range
   * - License validity period
   * @returns Boolean indicating if the selected year is the latest licensed year
   * @public
   */
  public isLatestLicensedYear(): boolean {
    const selectedCampaignYear = this.campaignYearStore.SelectedCampaignYear;
    if (!selectedCampaignYear) {
      return false;
    }
    const farmDate = this.dateService.getFarmDate();
    const campaignYearRange = this.campaignYearService.getCampaignYearByDate(farmDate);
    const selectedYearRange = this.campaignYearService.getCampaignYearRange(selectedCampaignYear.Year);
    return campaignYearRange.StartYear < campaignYearRange.EndYear
      && selectedCampaignYear.DefaultStart < selectedCampaignYear.DefaultEnd
      && selectedYearRange.StartYear < campaignYearRange.EndYear
      && this.campaignYearService.isCampaignYearIntersects(campaignYearRange.StartYear, campaignYearRange.EndYear, selectedYearRange);
  }

  /**
   * Retrieves a valid license for a specific feature module
   *
   * Filters licenses based on:
   * - Farm ID
   * - Feature module key
   *
   * License validation logic:
   * 1. Check trial expiration:
   *    - If trial expiration date exists, verify if it's still valid
   *    - Returns true if trial period is active
   * 2. Validate license period:
   *    - Checks if license has a valid 'ValidTo' date
   *    - Determines if license intersects with the current campaign year
   *    - Verifies that the license is not expired (current date is before or on ValidTo date)
   *
   * Specific validation steps:
   * - Get current farm date at midnight
   * - Check trial expiration (if applicable)
   * - Validate license period against campaign year
   * - Ensure license is current and not expired
   *
   * @param featureModulKey Feature module key to find license for
   * @returns Valid license or undefined
   * @private
   */
  private _getLicense(featureModulKey: ApFeatureModulKey): ILicense | undefined {
    if (!featureModulKey || !this.campaignYearStore.SelectedCampaignYear || !this.loginStore.SelectedFarm) {
      return undefined;
    }
    const selectedYearRange = this.campaignYearService.getCampaignYearRange(
      this.campaignYearStore.SelectedCampaignYear.Year
    );
    return this.licenseStore.Licenses.filter(x => x.FarmId === this.loginStore.SelectedFarmId)
      .filter(x => x.FeatureModul.Key === featureModulKey)
      .find(x => {
        const todayDate = this.dateService.getDateMidnight(this.dateService.getFarmDate());
        if (!!x.TrialExpireAt) {
          const TrialExpiredAtDayDifference = this.dateService.getDateDiff(new Date(x.TrialExpireAt),
            todayDate, DateDiffType.Days);
          if (TrialExpiredAtDayDifference >= 0) {
            return true;
          }
        }
        if (!x.ValidTo) {
          return false;
        }
        const validTo = new Date(x.ValidTo);
        const isLicenseIntersectsWithLatestCampaignYear = this.campaignYearService.isCampaignYearIntersects(
          new Date(x.ValidFrom), new Date(x.ValidTo), selectedYearRange);
        if (isLicenseIntersectsWithLatestCampaignYear) {
          const validToDayDifference = this.dateService.getDateDiff(validTo, todayDate, DateDiffType.Days);
          return validToDayDifference >= 0;
        }
        return false;
      });
  }
}
